import { useEffect } from 'react';

interface EmailKeyboardShortcutsProps {
  onCompose?: () => void;
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
  onSearch?: () => void;
  onNextConversation?: () => void;
  onPreviousConversation?: () => void;
  onNextMessage?: () => void;
  onPreviousMessage?: () => void;
  onMarkAsRead?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
  onRefresh?: () => void;
  enabled?: boolean;
}

export function useEmailKeyboardShortcuts({
  onCompose,
  onReply,
  onReplyAll,
  onForward,
  onSearch,
  onNextConversation,
  onPreviousConversation,
  onNextMessage,
  onPreviousMessage,
  onMarkAsRead,
  onArchive,
  onDelete,
  onRefresh,
  enabled = true,
}: EmailKeyboardShortcutsProps) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true'
      ) {
        return;
      }

      // Single key shortcuts
      switch (e.key.toLowerCase()) {
        case 'c':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onCompose?.();
          }
          break;

        case 'r':
          if (!e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            if (e.shiftKey) {
              onReplyAll?.();
            } else {
              onReply?.();
            }
          }
          break;

        case 'f':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onForward?.();
          }
          break;

        case '/':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onSearch?.();
          }
          break;

        case 'j':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onNextConversation?.();
          }
          break;

        case 'k':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onPreviousConversation?.();
          }
          break;

        case 'n':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onNextMessage?.();
          }
          break;

        case 'p':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onPreviousMessage?.();
          }
          break;

        case 'x':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onMarkAsRead?.();
          }
          break;

        case 'e':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onArchive?.();
          }
          break;

        case '#':
        case 'Delete':
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onDelete?.();
          }
          break;

        case 'Escape':
          // Close any open modals or return to conversation list
          break;
      }

      // Ctrl/Cmd combinations
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'enter':
            e.preventDefault();
            // Send email if in compose mode
            break;

          case 'r':
            e.preventDefault();
            onRefresh?.();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    enabled,
    onCompose,
    onReply,
    onReplyAll,
    onForward,
    onSearch,
    onNextConversation,
    onPreviousConversation,
    onNextMessage,
    onPreviousMessage,
    onMarkAsRead,
    onArchive,
    onDelete,
    onRefresh,
  ]);
}

// Keyboard shortcuts help text for UI
export const emailKeyboardShortcuts = [
  { key: 'C', description: 'Compose new email' },
  { key: 'R', description: 'Reply' },
  { key: 'Shift + R', description: 'Reply all' },
  { key: 'F', description: 'Forward' },
  { key: '/', description: 'Search' },
  { key: 'J', description: 'Next conversation' },
  { key: 'K', description: 'Previous conversation' },
  { key: 'N', description: 'Next message' },
  { key: 'P', description: 'Previous message' },
  { key: 'X', description: 'Mark as read/unread' },
  { key: 'E', description: 'Archive' },
  { key: '#', description: 'Delete' },
  { key: 'Ctrl/Cmd + R', description: 'Refresh' },
  { key: 'Ctrl/Cmd + Enter', description: 'Send email' },
];